import { DateTime } from 'luxon';

import type { TransactionItemProps, TransactionProps } from '../transactions/transactionsTypes';
import type { SpendingWatchlistProps, WatchlistFixedMetrics, WatchlistSortOrder } from './spendingWatchlistTypes';

type WatchlistDateRange = Pick<SpendingWatchlistProps, 'period' | 'endDate'>;
type SortableWatchlist = Pick<SpendingWatchlistProps, 'name' | 'createdAt' | 'yearToDate' | 'period' | 'endDate'>;
type WatchlistComparator = (a: SortableWatchlist, b: SortableWatchlist) => number;

/** Arrays sort in place; Immutable Lists return a new List with the same element type. */
type WatchlistCollection<T> = { sort(comparator: WatchlistComparator): T };

export type WatchlistMetricSignKind = 'spent' | 'average' | 'overspent' | 'unspent' | 'projected' | 'target';

/**
 * A watchlist can only be ordered or bucketed by its end date once it actually has one. A FIXED
 * period alone is not enough: without an endDate the watchlist can never end and cannot be ranked
 * against dated ones, so callers deciding whether end-date UI applies must use this, not `period`.
 */
export const hasWatchlistDateRange = (
  watchlist: WatchlistDateRange,
): watchlist is WatchlistDateRange & { period: 'FIXED'; endDate: string } =>
  watchlist.period === 'FIXED' && Boolean(watchlist.endDate);

export const isWatchlistEnded = (watchlist: WatchlistDateRange, today: DateTime): boolean =>
  hasWatchlistDateRange(watchlist) && DateTime.fromISO(watchlist.endDate).startOf('day') < today.startOf('day');

/**
 * The server caps `spentSoFar` at 12 months, understating any longer range. Callers that can compute
 * the true startDate→endDate total pass it here to get the dependent metrics re-derived from it.
 *
 * `targetAmount` is compared as a magnitude: stored in ledger sign, so using it raw reads as a large
 * overspend for any negative target.
 */
export function computeEffectiveMetrics(
  fixedMetrics: WatchlistFixedMetrics,
  clientSpentSoFar: number | null | undefined,
  targetAmount: number | null | undefined,
): WatchlistFixedMetrics;
export function computeEffectiveMetrics(
  fixedMetrics: null,
  clientSpentSoFar: number | null | undefined,
  targetAmount: number | null | undefined,
): null;
export function computeEffectiveMetrics(
  fixedMetrics: WatchlistFixedMetrics | null,
  clientSpentSoFar: number | null | undefined,
  targetAmount: number | null | undefined,
): WatchlistFixedMetrics | null;
export function computeEffectiveMetrics(
  fixedMetrics: WatchlistFixedMetrics | null,
  clientSpentSoFar: number | null | undefined,
  targetAmount: number | null | undefined,
): WatchlistFixedMetrics | null {
  if (!fixedMetrics || clientSpentSoFar == null) return fixedMetrics;
  const spentAbs = Math.abs(clientSpentSoFar);
  const { daysElapsed, totalDays, totalMonths, timeStep } = fixedMetrics;
  return {
    ...fixedMetrics,
    spentAbs,
    dailyAverage: timeStep === 'days' && totalDays > 0 ? spentAbs / totalDays : null,
    monthlyAverage: timeStep === 'months' && totalMonths > 0 ? spentAbs / totalMonths : null,
    projected: daysElapsed > 0 && totalDays > 0 ? (spentAbs / daysElapsed) * totalDays : null,
    remaining: targetAmount != null ? Math.abs(targetAmount) - spentAbs : fixedMetrics.remaining,
  };
}

/**
 * Watchlist figures render as magnitudes. Preserve an inflow's ledger direction with a leading
 * "+" only on income metrics derived from that amount: spent, average, and projected.
 * Targets and outcome amounts (overspent/unspent) remain unsigned.
 */
export function shouldShowPositiveWatchlistMetricSign(
  netAmount: number | null | undefined,
  metric: WatchlistMetricSignKind,
): boolean {
  return netAmount != null && netAmount > 0 && (metric === 'spent' || metric === 'average' || metric === 'projected');
}

export function getFixedWatchlistTimeStep(
  startDate: string | null | undefined,
  endDate: string | null | undefined,
): 'days' | 'months' {
  // An unresolvable range (no endDate) can't compute a monthly denominator, so fall back to daily
  // granularity, whose totalDays safely resolves through today.
  if (!startDate || !endDate) return 'days';
  const durationDays = DateTime.fromISO(endDate).diff(DateTime.fromISO(startDate), 'days').days;
  return durationDays < 30 ? 'days' : 'months';
}

/**
 * Number of calendar-month buckets in a fixed date range. This matches the periods emitted for
 * monthly charts, including both partial boundary months.
 * Returns 0 for an invalid, missing, or reversed range.
 */
export function computeMonthsInRange(startDate: string | null | undefined, endDate: string | null | undefined): number {
  if (!startDate || !endDate) return 0;
  const start = DateTime.fromISO(startDate);
  const end = DateTime.fromISO(endDate);
  if (!start.isValid || !end.isValid || end < start) return 0;
  return Math.round(end.startOf('month').diff(start.startOf('month'), 'months').months) + 1;
}

// Every comparator below coerces missing values to a definite one. A comparator that returns NaN
// is inconsistent, and `sort` answers that by leaving the whole list in its original order, so a
// single absent name/date/amount would silently defeat the sort for every other watchlist too.

// Whitespace is stripped so "Sun Trust" and "SunTrust" collate together.
const compareNames: WatchlistComparator = (a, b) =>
  (a.name || '').replace(/\s/g, '').localeCompare((b.name || '').replace(/\s/g, ''));

const toMillis = (isoDate: string | null | undefined): number =>
  isoDate ? DateTime.fromISO(isoDate).toMillis() || 0 : 0;

const compareCreatedAt: WatchlistComparator = (a, b) => toMillis(a.createdAt) - toMillis(b.createdAt);

const compareAmount: WatchlistComparator = (a, b) => Math.abs(a.yearToDate || 0) - Math.abs(b.yearToDate || 0);

// A watchlist with no end date cannot be ordered by one, so it sinks below the timed ones and
// falls back to name order. Keeps both end-date directions agreeing on that tail.
const compareEndDates =
  (ascending: boolean): WatchlistComparator =>
  (a, b) => {
    const aIsTimed = hasWatchlistDateRange(a);
    const bIsTimed = hasWatchlistDateRange(b);
    if (aIsTimed && bIsTimed) {
      const diff = toMillis(a.endDate) - toMillis(b.endDate);
      return ascending ? diff : -diff;
    }
    if (aIsTimed) return -1;
    if (bIsTimed) return 1;
    return compareNames(a, b);
  };

export const sortWatchListByDate = <T extends WatchlistCollection<T>>(watchList: T): T =>
  watchList.sort((a, b) => compareCreatedAt(b, a));

export const sortWatchListByDateAsc = <T extends WatchlistCollection<T>>(watchList: T): T =>
  watchList.sort(compareCreatedAt);

export const sortWatchListAlphabetically = <T extends WatchlistCollection<T>>(watchList: T): T =>
  watchList.sort(compareNames);

export const sortWatchListAlphabeticallyDesc = <T extends WatchlistCollection<T>>(watchList: T): T =>
  watchList.sort((a, b) => compareNames(b, a));

export const sortWatchListByAmountDesc = <T extends WatchlistCollection<T>>(watchList: T): T =>
  watchList.sort((a, b) => compareAmount(b, a));

export const sortWatchListByAmountAsc = <T extends WatchlistCollection<T>>(watchList: T): T =>
  watchList.sort(compareAmount);

export const sortWatchListByEndingSoonest = <T extends WatchlistCollection<T>>(watchList: T): T =>
  watchList.sort(compareEndDates(true));

export const sortWatchListByEndingLatest = <T extends WatchlistCollection<T>>(watchList: T): T =>
  watchList.sort(compareEndDates(false));

export function sortWatchList<T extends WatchlistCollection<T> | null | undefined>(
  swlData: T,
  swlOrder?: WatchlistSortOrder,
): T;
export function sortWatchList<T extends WatchlistCollection<T>>(
  swlData: T | null | undefined,
  swlOrder?: WatchlistSortOrder,
): T | null | undefined {
  if (!swlData) {
    return swlData;
  }
  switch (swlOrder) {
    case 'BY_DATE':
      return sortWatchListByDate(swlData);
    case 'BY_DATE_ASC':
      return sortWatchListByDateAsc(swlData);
    case 'BY_ALPHABET':
      return sortWatchListAlphabetically(swlData);
    case 'BY_ALPHABET_DESC':
      return sortWatchListAlphabeticallyDesc(swlData);
    case 'BY_ENDING_SOONEST':
      return sortWatchListByEndingSoonest(swlData);
    case 'BY_ENDING_LATEST':
      return sortWatchListByEndingLatest(swlData);
    case 'BY_AMOUNT_ASC':
      return sortWatchListByAmountAsc(swlData);
    case 'BY_AMOUNT_DESC':
      return sortWatchListByAmountDesc(swlData);
    default:
      return sortWatchListByDate(swlData);
  }
}

export type SeparatedWatchlistTransactions<TTransaction, TItem> = {
  txnsWithoutUpcoming: TTransaction[];
  txnsUpcoming: TTransaction[];
  txnsItemsWithoutUpcoming: TItem[];
  txnsItemsUpcoming: TItem[];
};

// Transaction items also fill the transaction buckets, with each parent listed once.
export function separateFutureAndPastTxns<T extends TransactionProps>(
  txnItemsArray: readonly T[] | null | undefined,
): SeparatedWatchlistTransactions<T, T>;
export function separateFutureAndPastTxns<T extends TransactionItemProps>(
  txnItemsArray: readonly T[] | null | undefined,
): SeparatedWatchlistTransactions<T['transaction'], T>;
export function separateFutureAndPastTxns(
  txnItemsArray: readonly (TransactionProps | TransactionItemProps)[] | null | undefined,
): SeparatedWatchlistTransactions<TransactionProps, TransactionProps | TransactionItemProps> {
  const today = DateTime.local().toISODate();
  return (txnItemsArray || []).reduce<
    SeparatedWatchlistTransactions<TransactionProps, TransactionProps | TransactionItemProps>
  >(
    (accumulator, item) => {
      const txn = 'transaction' in item ? item.transaction : item;
      const transactionDate = txn.postedOn || txn.stDueOn;
      const isFuture =
        (transactionDate != null && today != null && transactionDate > today) ||
        txn.source === 'SCHEDULED_TRANSACTION_PENDING' ||
        (txn.subtype === 'REFUND' && txn.state === 'PENDING');
      if (isFuture) {
        accumulator.txnsItemsUpcoming.push(item);
        if (!accumulator.txnsUpcoming.includes(txn)) {
          accumulator.txnsUpcoming.push(txn);
        }
      } else {
        accumulator.txnsItemsWithoutUpcoming.push(item);
        if (!accumulator.txnsWithoutUpcoming.includes(txn)) {
          accumulator.txnsWithoutUpcoming.push(txn);
        }
      }
      return accumulator;
    },
    {
      txnsWithoutUpcoming: [],
      txnsUpcoming: [],
      txnsItemsWithoutUpcoming: [],
      txnsItemsUpcoming: [],
    },
  );
}
